import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { profileApi } from '@/services/profileApi';
import { learningPlanApi, userLearningPlanApi, userLessonApi, userNudgeApi } from '@/services/learningManagementApi';
import { coachApi } from '@/services/coachApi';
import { createApiService } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import type { ActivePlanDashboardResponse } from '@/types/learningManagement';
import type { UserProfile, Bio } from '@/types/userProfile';
import type { LearningPlan } from '@/types/learningManagement';
import type { CoachFolder, CoachGoalsResponse, CoachNudgeResponse, ConversationListItem, ConversationsListResponse } from '@/types/coach';

export interface CoachMessageDisplay {
  role: 'user' | 'assistant';
  content: string;
  /** Unique message ID from backend (for feedback/rating). Present when loaded from API or after refetch. */
  messageId?: string;
  sources?: { type: string; id: string; title: string }[];
  followUps?: string[];
  evaluation?: { confidenceScore?: number; qualityTier?: string; piiSafe?: boolean; agentName?: string; durationMs?: number; modelUsed?: string; flags?: string[] } | null;
}

const LOCAL_USER_INFO_KEY = 'five_ai_local_user_info';
const COACH_SESSION_KEY = 'five_coach_session';

interface StoredSession {
  sessionId: string;
  conversationId: string | null;
  userId: string;
}

function getUserIdFromStorage(): string | null {
  try {
    const raw = localStorage.getItem(LOCAL_USER_INFO_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed.signUpEmail ?? parsed.email ?? null;
  } catch {
    return null;
  }
}

function setUserIdInStorage(uid: string): void {
  try {
    localStorage.setItem(LOCAL_USER_INFO_KEY, JSON.stringify({ email: uid, signUpEmail: uid }));
  } catch {
    // ignore
  }
}

function loadSessionFromStorage(): StoredSession | null {
  try {
    const raw = localStorage.getItem(COACH_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredSession;
    if (parsed.sessionId && parsed.userId) return parsed;
    return null;
  } catch {
    return null;
  }
}

function saveSessionToStorage(session: StoredSession | null): void {
  if (!session) {
    localStorage.removeItem(COACH_SESSION_KEY);
    return;
  }
  localStorage.setItem(COACH_SESSION_KEY, JSON.stringify(session));
}

const AUTO_RESUME_DAYS = 7;

/** True if the ISO date string is within the last N days from now. */
function isUpdatedWithinDays(updatedAt: string | undefined, days: number): boolean {
  if (!updatedAt) return false;
  const updated = new Date(updatedAt).getTime();
  const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
  return updated >= cutoff;
}

/** Treat as non-resumable only when status is explicitly ended. */
function isResumableStatus(status: string | undefined): boolean {
  if (!status) return true;
  const ended = ['COMPLETED', 'ARCHIVED', 'DELETED', 'CLOSED'];
  return !ended.includes(status.toUpperCase());
}

/** Canonical id from list item (API may use id, _id, or conversation_id). */
function getConvId(item: ConversationListItem & { _id?: string; conversation_id?: string }): string {
  return item.id ?? item._id ?? item.conversation_id ?? '';
}

/** Get folder ID from item (API may use folderId or folder_id). */
function getItemFolderId(c: { folderId?: string | null; folder_id?: string | null }): string | null | undefined {
  const v = c.folderId ?? (c as { folder_id?: string | null }).folder_id;
  return v === undefined || v === null ? undefined : (v as string);
}

/** Deduplicate by id, keep most recent (by updatedAt/createdAt). Normalizes id and folderId on each item. */
function dedupeConversations(list: (ConversationListItem & { _id?: string; conversation_id?: string; folder_id?: string | null })[]): ConversationListItem[] {
  const byId = new Map<string, ConversationListItem>();
  for (const c of list) {
    const id = getConvId(c);
    if (!id) continue;
    const folderId = getItemFolderId(c);
    const normalized = { ...c, id, folderId } as ConversationListItem;
    const existing = byId.get(id);
    const cTime = new Date((c.updatedAt ?? c.createdAt) ?? 0).getTime();
    const exTime = existing ? new Date((existing.updatedAt ?? existing.createdAt) ?? 0).getTime() : 0;
    if (!existing || cTime > exTime) {
      byId.set(id, normalized);
    }
  }
  return Array.from(byId.values());
}

/** Returns the most recent conversation to auto-resume: within N days and resumable status. */
function getMostRecentConversationWithinDays(
  conversations: ConversationListItem[],
  days: number
): ConversationListItem | null {
  for (const c of conversations) {
    if (!isResumableStatus(c.status)) continue;
    const dateStr = c.updatedAt ?? c.createdAt;
    if (!isUpdatedWithinDays(dateStr, days)) continue;
    return c;
  }
  return null;
}

export interface CoachState {
  userId: string | null;
  profile: UserProfile | null;
  learningPlan: LearningPlan | null;
  /** Active plan dashboard (progress, days completed, current day) for journey status. */
  dashboard: ActivePlanDashboardResponse | null;
  sessionId: string | null;
  conversationId: string | null;
  folders: CoachFolder[];
  /** When set, conversation list is filtered to this folder; null = show all (Recent). */
  selectedFolderId: string | null;
  conversations: ConversationListItem[];
  messages: CoachMessageDisplay[];
  nudge: CoachNudgeResponse | null;
  goals: CoachGoalsResponse | null;
  loading: boolean;
  error: string | null;
  suggestedQuestions: string[];
  /** True when we resumed/selected this conversation and attempted to load messages (so empty = reload, not welcome). */
  messagesLoadAttemptedForCurrentConv: boolean;
  /** Error when loading messages for current conversation (e.g. network, 500). */
  messagesLoadError: string | null;
  /** Day number -> lesson title from API; used by context panel and chat welcome so day labels match. */
  lessonTitlesByDay: Record<number, string>;
  /** Active wins from GET .../users/{userId}/wins. Use primaryWin (wins[0]) for primary display. */
  activeWins: { id?: string; name: string; description?: string; summary?: string }[] | null;
  /** First of activeWins; use for primary win display on Coach. */
  primaryWin: { name: string; description?: string; summary?: string } | null;
}

interface CoachContextValue extends CoachState {
  setMessages: React.Dispatch<React.SetStateAction<CoachMessageDisplay[]>>;
  refetchNudge: () => Promise<void>;
  refetchGoals: () => Promise<void>;
  refetchFolders: () => Promise<void>;
  /** Refetch the active-plan dashboard (progress + day status) after lesson actions. */
  refetchDashboard: () => Promise<ActivePlanDashboardResponse | null>;
  refetchConversations: () => Promise<void>;
  /** Set folder filter for conversation list (null = all). List refetches when this changes. */
  setSelectedFolderId: (folderId: string | null) => void;
  searchConversations: (query: string, limit?: number) => Promise<ConversationListItem[]>;
  loadMessagesForConversation: (conversationId: string, options?: { afterStream?: boolean }) => Promise<void>;
  startNewChat: () => Promise<void>;
  selectConversation: (conversationId: string) => Promise<void>;
  persistSessionFromDone: (conversationId: string, conversationTitle?: string | null) => void;
  updateConversationTitle: (conversationId: string, title: string) => Promise<void>;
  deleteConversation: (conversationId: string) => Promise<void>;
  /** Send a user message from the context panel (e.g. start lesson). No-op until CoachMain registers a handler. */
  sendUserMessage: (text: string) => void;
  /** Register the handler used when sendUserMessage is called. Called by CoachMain. */
  registerSendUserMessage: (handler: ((text: string) => void) | null) => void;
}

const CoachContext = createContext<CoachContextValue | null>(null);

// eslint-disable-next-line react-refresh/only-export-components
export function useCoach() {
  const ctx = useContext(CoachContext);
  if (!ctx) throw new Error('useCoach must be used within CoachProvider');
  return ctx;
}

/** Use coach context when available (e.g. banner inside/outside provider). Returns null when not inside CoachProvider. */
// eslint-disable-next-line react-refresh/only-export-components
export function useCoachOptional(): CoachContextValue | null {
  return useContext(CoachContext);
}

/** SessionStorage key for "start lesson" when navigating from Learning Plan page to Coach. */
export const COACH_START_LESSON_KEY = 'coach_start_lesson';

export const CoachProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user: authUser, isAuthenticated } = useAuth();
  const storageUserId = getUserIdFromStorage();
  const canonicalUserId = (isAuthenticated && authUser?.email ? authUser.email : storageUserId) ?? null;
  useEffect(() => {
    if (isAuthenticated && authUser?.email) setUserIdInStorage(authUser.email);
  }, [isAuthenticated, authUser?.email]);
  const [userId, setUserId] = useState<string | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [learningPlan, setLearningPlan] = useState<LearningPlan | null>(null);
  const [dashboard, setDashboard] = useState<ActivePlanDashboardResponse | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [folders, setFolders] = useState<CoachFolder[]>([]);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [allConversations, setAllConversations] = useState<ConversationListItem[]>([]);
  const [messages, setMessages] = useState<CoachMessageDisplay[]>([]);
  const [nudge, setNudge] = useState<CoachNudgeResponse | null>(null);
  const [goals, setGoals] = useState<CoachGoalsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [messagesLoadAttemptedForCurrentConv, setMessagesLoadAttemptedForCurrentConv] = useState(false);
  const [messagesLoadError, setMessagesLoadError] = useState<string | null>(null);
  const [lessonTitlesByDay, setLessonTitlesByDay] = useState<Record<number, string>>({});
  const [activeWins, setActiveWins] = useState<{ id?: string; name: string; description?: string; summary?: string }[] | null>(null);
  const sendUserMessageRef = useRef<((text: string) => void) | null>(null);

  const primaryWin = useMemo(() => (activeWins && activeWins.length > 0 ? { name: activeWins[0].name, description: activeWins[0].description, summary: activeWins[0].summary } : null), [activeWins]);

  const suggestedQuestions = useMemo(() => {
    const painPoints = (profile?.bio as Bio | undefined)?.painPoints;
    if (painPoints?.length) {
      return painPoints
        .slice(0, 5)
        .map((p) => (p.description ? `Help me with: ${p.description}` : ''))
        .filter(Boolean);
    }
    return [
      "How can I improve stakeholder buy-in for my initiatives?",
      "Help me prepare for my next review",
      "What's my 7-day growth plan?",
    ];
  }, [profile?.bio]);

  /** Index for cycling through stored nudges on refresh. */
  const nudgeCycleIndexRef = useRef(0);
  const storedNudgesRef = useRef<Record<string, unknown>[]>([]);

  const refetchNudge = useCallback(async () => {
    if (!userId) return;
    try {
      // Try fetching stored nudges from learning-goal-service first
      const userNudges = await userNudgeApi.getByUser(userId);
      if (userNudges.length > 0) {
        storedNudgesRef.current = userNudges;
        // Use stored nudges — prefer PLANNED/ACTIVE, fall back to all
        const activeNudges = userNudges.filter((n) => {
          const status = ((n.status ?? '') as string).toUpperCase();
          return status === 'PLANNED' || status === 'ACTIVE' || status === 'PUBLISHED';
        });
        const pool = activeNudges.length > 0 ? activeNudges : userNudges;
        // Cycle to next nudge on each refresh
        const idx = nudgeCycleIndexRef.current % pool.length;
        nudgeCycleIndexRef.current = idx + 1;
        const picked = pool[idx];

        // Map stored nudge fields to CoachNudgeResponse shape
        const nudgeText = (picked.content ?? picked.nudgeText ?? picked.nudge_text ?? picked.title ?? picked.description ?? '') as string;
        const nudgeType = (picked.nudgeType ?? picked.nudge_type ?? picked.category ?? '') as string;
        const followupPrompts = (picked.followupPrompts ?? picked.followup_prompts ?? picked.actionItems ?? picked.action_items ?? []) as string[];

        setNudge({
          nudgeText,
          nudgeType,
          personalizationFactors: Array.isArray(picked.personalizationFactors)
            ? picked.personalizationFactors as string[]
            : undefined,
          // Attach follow-up prompts for the panel to use
          ...(followupPrompts.length > 0 ? { followup_prompts: followupPrompts } : {}),
        } as CoachNudgeResponse & { followup_prompts?: string[] });
        return;
      }
    } catch (e) {
      console.warn('Failed to fetch stored nudges, falling back to agent', e);
    }
    // Fallback to agent-generated nudge
    try {
      const data = await coachApi.getNudge(userId);
      setNudge(data);
    } catch (e) {
      console.warn('Failed to refetch nudge', e);
    }
  }, [userId]);

  const refetchGoals = useCallback(async () => {
    if (!userId) return;
    try {
      const service = createApiService('/api/learning-goal');

      // Fetch active + all goals from learning-goal-service
      const [activeRes, allRes] = await Promise.all([
        service.get<unknown>(`/api/v1/personal-goals/users/${encodeURIComponent(userId)}/active`).catch(() => null),
        service.get<unknown>(`/api/v1/personal-goals/users/${encodeURIComponent(userId)}`).catch(() => null),
      ]);

      // Unwrap: backend may return array, { content: [] }, or { data: [] }
      const unwrap = (res: unknown): Record<string, unknown>[] => {
        if (!res) return [];
        if (Array.isArray(res)) return res as Record<string, unknown>[];
        if (typeof res === 'object' && res !== null) {
          const obj = res as Record<string, unknown>;
          const list = obj.content ?? obj.data ?? obj.goals ?? obj.results;
          if (Array.isArray(list)) return list as Record<string, unknown>[];
        }
        return [];
      };

      const active = unwrap(activeRes);
      const all = unwrap(allRes);

      if (active.length > 0 || all.length > 0) {
        // Goals found in DB — use them
        // Merge: short-term = active weekly/short-term, long-term = active monthly/long-term
        const shortTerm = active.filter((g) => {
          const goalType = ((g.goalType ?? g.goal_type ?? '') as string).toUpperCase();
          const timeFrame = ((g.timeFrame ?? g.time_frame ?? '') as string).toUpperCase();
          return goalType === 'SHORT_TERM' || timeFrame === 'WEEKLY' || timeFrame === 'DAILY';
        });
        const longTerm = active.filter((g) => {
          const goalType = ((g.goalType ?? g.goal_type ?? '') as string).toUpperCase();
          const timeFrame = ((g.timeFrame ?? g.time_frame ?? '') as string).toUpperCase();
          return goalType === 'LONG_TERM' || timeFrame === 'MONTHLY' || timeFrame === 'QUARTERLY';
        });
        // If no categorization, split by position
        const uncategorized = active.filter((g) => !shortTerm.includes(g) && !longTerm.includes(g));

        const completedGoals = all.filter((g) => ((g.status ?? '') as string).toUpperCase() === 'COMPLETED').slice(0, 3);

        setGoals({
          shortTermGoals: [...shortTerm, ...uncategorized].slice(0, 5).map((g) => ({
            title: (g.title ?? g.goal_title ?? '') as string,
            description: (g.description ?? g.goal_description ?? '') as string,
            ...g,
          })),
          longTermGoals: longTerm.slice(0, 3).map((g) => ({
            title: (g.title ?? g.goal_title ?? '') as string,
            description: (g.description ?? g.goal_description ?? '') as string,
            ...g,
          })),
          completedGoals: completedGoals.map((g) => ({
            title: (g.title ?? g.goal_title ?? '') as string,
            description: (g.description ?? g.goal_description ?? '') as string,
            ...g,
          })),
        } as CoachGoalsResponse & { completedGoals?: unknown[] });
        return;
      }
    } catch (e) {
      console.warn('Failed to fetch goals from learning-goal-service, falling back to agent', e);
    }
    // Fallback 1: Use bio.professionalGoals if available (from onboarding)
    if (profile) {
      const bioAny = profile.bio as Record<string, unknown> | undefined;
      const bioGoals = (bioAny?.professionalGoals ?? bioAny?.professional_goals ?? []) as string[];
      if (bioGoals.length > 0) {
        const shortTermGoals: { title: string; description: string }[] = [];
        const longTermGoals: { title: string; description: string }[] = [];
        for (const raw of bioGoals) {
          const match = raw.match(/^\[(SHORT_TERM|LONG_TERM)]\s*(.+)$/);
          if (match) {
            const [, type, title] = match;
            (type === 'LONG_TERM' ? longTermGoals : shortTermGoals).push({ title, description: '' });
          } else {
            shortTermGoals.push({ title: raw, description: '' });
          }
        }
        setGoals({ shortTermGoals, longTermGoals } as CoachGoalsResponse);
        return;
      }
    }
    // Fallback 2: agent-generated goals
    try {
      const agentGoals = await coachApi.getGoals(userId);
      setGoals(agentGoals);
    } catch (e) {
      console.warn('Failed to refetch goals', e);
    }
  }, [userId, profile]);

  const refetchFolders = useCallback(async () => {
    if (!userId) return;
    try {
      const data = await coachApi.getFolders(userId);
      setFolders(data ?? []);
    } catch (e) {
      console.warn('Failed to refetch folders', e);
    }
  }, [userId]);

  const refetchDashboard = useCallback(async () => {
    if (!userId) return null;
    try {
      const data = await userLearningPlanApi.getDashboard(userId);
      setDashboard((data as ActivePlanDashboardResponse | null) ?? null);
      return (data as ActivePlanDashboardResponse | null) ?? null;
    } catch (e) {
      console.warn('Failed to refetch dashboard', e);
      return null;
    }
  }, [userId]);

  const sortConversationsByRecency = useCallback((list: ConversationListItem[]) => {
    return [...list].sort((a, b) => {
      const aTime = new Date(a.updatedAt ?? a.createdAt ?? 0).getTime();
      const bTime = new Date(b.updatedAt ?? b.createdAt ?? 0).getTime();
      return bTime - aTime;
    });
  }, []);

  /** Fetch all conversations (no folder filter). Filtering by folder is done client-side. */
  const refetchConversations = useCallback(async () => {
    if (!userId) return;
    try {
      const data = await coachApi.getConversations(userId, { limit: 100 });
      const raw = data as unknown as Record<string, unknown>;
      const list = data.conversations ?? raw.content ?? raw.results ?? [];
      const deduped = dedupeConversations(Array.isArray(list) ? list : []);
      const sorted = sortConversationsByRecency(deduped);
      setAllConversations(sorted);
    } catch (e) {
      console.warn('Failed to refetch conversations', e);
    }
  }, [userId, sortConversationsByRecency]);

  /** Conversations filtered by selected folder. Recent (null) = all; folder selected = only that folder. */
  const conversations = useMemo(() => {
    if (!selectedFolderId) return allConversations;
    return allConversations.filter(
      (c) => (c.folderId ?? (c as { folder_id?: string | null }).folder_id) === selectedFolderId
    );
  }, [allConversations, selectedFolderId]);

  const searchConversations = useCallback(async (query: string, limit = 10): Promise<ConversationListItem[]> => {
    if (!userId || !query.trim()) return [];
    try {
      const data = await coachApi.searchConversations(userId, query.trim(), limit);
      return sortConversationsByRecency(data.conversations ?? []);
    } catch (e) {
      console.warn('Failed to search conversations', e);
      return [];
    }
  }, [userId, sortConversationsByRecency]);

  /** Extract messages array from various API response shapes (agent, Spring Page, etc.). */
  const getMessagesFromResponse = useCallback((data: unknown): unknown[] => {
    if (data == null || typeof data !== 'object') return [];
    const o = data as Record<string, unknown>;
    const raw =
      o.messages ??
      o.content ??
      o.data ??
      o.items ??
      o.results ??
      (Array.isArray(o) ? o : null);
    return Array.isArray(raw) ? raw : [];
  }, []);

  const loadMessagesForConversation = useCallback(async (convId: string, options?: { afterStream?: boolean }) => {
    if (!options?.afterStream) {
      setMessagesLoadAttemptedForCurrentConv(true);
      setMessagesLoadError(null);
    }
    try {
      const data = await coachApi.getConversationMessages(convId);
      const arr = getMessagesFromResponse(data) as unknown[];
      const mapped: CoachMessageDisplay[] = arr.map((m) => {
        const msg = m as Record<string, unknown>;
        const meta = (msg.metadata ?? {}) as Record<string, unknown>;
        const src = meta.sources as { type?: string; id?: string; title?: string }[] | undefined;
        const fu = (meta.suggestedFollowUps ?? meta.suggested_follow_ups) as string[] | undefined;
        const sources: { type: string; id: string; title: string }[] | undefined = Array.isArray(src)
          ? src
              .filter((s): s is { type?: string; id: string; title?: string } => !!s?.id)
              .map((s) => ({ type: s.type ?? '', id: s.id, title: s.title ?? '' }))
          : undefined;
        return {
          role: (msg.role === 'USER' ? 'user' : 'assistant') as 'user' | 'assistant',
          content: String(msg.content ?? msg.text ?? ''),
          messageId: (msg.id ?? msg.messageId ?? msg._id) as string | undefined,
          sources,
          followUps: Array.isArray(fu) ? fu : undefined,
        };
      });
      if (options?.afterStream) {
        setMessages((prev) => {
          if (mapped.length < prev.length) return prev;
          // Merge: preserve followUps, sources, evaluation from prev for assistant messages (API often doesn't store them)
          const prevAssistants = prev.filter((p) => p.role === 'assistant');
          let aIdx = 0;
          const merged = mapped.map((apiMsg) => {
            if (apiMsg.role !== 'assistant') return apiMsg;
            const prevMatch = prevAssistants[aIdx++];
            if (!prevMatch?.followUps?.length && !prevMatch?.sources?.length) return apiMsg;
            return {
              ...apiMsg,
              followUps: prevMatch.followUps ?? apiMsg.followUps,
              sources: prevMatch.sources ?? apiMsg.sources,
              evaluation: prevMatch.evaluation ?? apiMsg.evaluation,
            };
          });
          return merged;
        });
      } else {
        setMessages(mapped);
        setMessagesLoadError(null);
      }
    } catch (e) {
      console.warn('Failed to load conversation messages', e);
      if (!options?.afterStream) {
        setMessages([]);
        setMessagesLoadError(e instanceof Error ? e.message : 'Failed to load messages');
      }
    }
  }, [getMessagesFromResponse]);

  const startNewChat = useCallback(async () => {
    if (!userId) return;
    saveSessionToStorage(null);
    setMessagesLoadAttemptedForCurrentConv(false);
    setMessagesLoadError(null);
    setMessages([]);
    const sid = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `session-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    try {
      const session = await coachApi.startSession({ userId, sessionId: sid });
      setSessionId(session.sessionId);
      setConversationId(session.conversationId ?? null);
      if (session.conversationId) {
        saveSessionToStorage({ sessionId: session.sessionId, conversationId: session.conversationId, userId });
      }
      await refetchConversations();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to start new chat');
    }
  }, [userId, refetchConversations]);

  const selectConversation = useCallback(async (convId: string) => {
    if (!userId || !convId) return;
    if (conversationId === convId) {
      // Same conversation: refresh messages (e.g. after refresh or stale view)
      await loadMessagesForConversation(convId);
      return;
    }
    const sid = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `session-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    try {
      const session = await coachApi.startSession({ userId, sessionId: sid, conversationId: convId });
      setSessionId(session.sessionId);
      setConversationId(session.conversationId ?? convId);
      saveSessionToStorage({ sessionId: session.sessionId, conversationId: session.conversationId ?? convId, userId });
      await loadMessagesForConversation(convId);
      await refetchConversations();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to switch conversation');
    }
  }, [userId, conversationId, loadMessagesForConversation, refetchConversations]);

  const persistSessionFromDone = useCallback((convId: string, conversationTitle?: string | null) => {
    if (!userId || !sessionId) return;
    setConversationId(convId);
    saveSessionToStorage({ sessionId, conversationId: convId, userId });

    if (conversationTitle) {
      setAllConversations((prev) => {
        const idx = prev.findIndex((c) => getConvId(c as ConversationListItem & { _id?: string; conversation_id?: string }) === convId);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = { ...next[idx], title: conversationTitle };
          return next;
        }
        return dedupeConversations([{ id: convId, title: conversationTitle, status: 'ACTIVE' }, ...prev]);
      });
    }
  }, [userId, sessionId]);

  const updateConversationTitle = useCallback(async (convId: string, title: string) => {
    if (!userId) return;
    try {
      await coachApi.updateConversationTitle(convId, { userId, title });
      setAllConversations((prev) => {
        const idx = prev.findIndex((c) => getConvId(c as ConversationListItem & { _id?: string; conversation_id?: string }) === convId);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = { ...next[idx], title };
          return next;
        }
        return prev;
      });
    } catch (e) {
      console.warn('Failed to update conversation title', e);
    }
  }, [userId]);

  const deleteConversation = useCallback(async (convId: string) => {
    if (!userId) return;
    try {
      await coachApi.deleteConversation(convId, userId);
      setAllConversations((prev) => prev.filter((c) => c.id !== convId));
      if (conversationId === convId) {
        saveSessionToStorage(null);
        setMessages([]);
        await startNewChat();
      }
    } catch (e) {
      console.warn('Failed to delete conversation', e);
    }
  }, [userId, conversationId, startNewChat]);

  const sessionRef = useRef({ sessionId, userId });
  useEffect(() => {
    sessionRef.current = { sessionId, userId };
  }, [sessionId, userId]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      const { sessionId: sid, userId: uid } = sessionRef.current;
      if (sid && uid) {
        coachApi.endSessionOnPageUnload({ userId: uid, sessionId: sid, triggerRecalc: true });
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      const { sessionId: sid, userId: uid } = sessionRef.current;
      if (sid && uid) {
        coachApi.endSession({ userId: uid, sessionId: sid, triggerRecalc: true }).catch(() => {});
      }
    };
  }, []);

  useEffect(() => {
    const uid = canonicalUserId;
    if (!uid) {
      setError('No user found. Complete onboarding first.');
      setLoading(false);
      return;
    }
    setUserId(uid);

    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        // Use allSettled so one failing service doesn't block conversations from showing.
        // When uid looks like email, try getByEmail first so profile (and bio pain points/skills) loads correctly.
        const loadProfile = uid.includes('@')
          ? profileApi.getByEmail(uid).catch((e: unknown) => {
              const err = e as { status?: number };
              if (err?.status === 404) return profileApi.getById(uid);
              throw e;
            })
          : profileApi.getById(uid).catch((e: unknown) => {
              const err = e as { status?: number };
              if (err?.status === 404 && uid.includes('@')) return profileApi.getByEmail(uid);
              throw e;
            });
        const results = await Promise.allSettled([
          loadProfile,
          learningPlanApi.getByUser(uid),
          userLearningPlanApi.getDashboard(uid),
          userLearningPlanApi.getWinsForUser(uid),
          coachApi.getFolders(uid),
          coachApi.getConversations(uid, { limit: 50 }),
        ]);
        if (cancelled) return;
        const profileRes = results[0].status === 'fulfilled' ? results[0].value : null;
        const planRes = results[1].status === 'fulfilled' ? results[1].value : null;
        const dashboardRes = results[2].status === 'fulfilled' ? results[2].value : null;
        const winsRes = results[3].status === 'fulfilled' ? results[3].value : null;
        const folderList = results[4].status === 'fulfilled' ? results[4].value : null;
        const convList = results[5].status === 'fulfilled' ? results[5].value : null;
        if (!profileRes) {
          const err = (results[0] as PromiseRejectedResult)?.reason;
          throw err ?? new Error('Profile not found');
        }
        setProfile(profileRes as UserProfile);
        setLearningPlan((planRes as LearningPlan | null) ?? null);
        setDashboard((dashboardRes as ActivePlanDashboardResponse | null) ?? null);
        setActiveWins(Array.isArray(winsRes) ? winsRes : null);
        setFolders((folderList as CoachFolder[] | null) ?? []);
        const rawList = (convList as ConversationsListResponse | null)?.conversations ?? (convList as { content?: unknown[] } | null)?.content ?? [];
        const deduped = dedupeConversations(
          (Array.isArray(rawList) ? rawList : []) as (ConversationListItem & { _id?: string; conversation_id?: string })[]
        );
        const convListArr = sortConversationsByRecency(deduped);
        setAllConversations(convListArr);
        // Load nudges from stored learning nudges, goals from bio
        try {
          const storedNudges = await userNudgeApi.getByUser(uid);
          if (storedNudges.length > 0) {
            const activeNudges = storedNudges.filter((n) => {
              const st = ((n.status ?? '') as string).toUpperCase();
              return st === 'PLANNED' || st === 'ACTIVE' || st === 'PUBLISHED';
            });
            const pool = activeNudges.length > 0 ? activeNudges : storedNudges;
            // Pick first nudge for display
            const picked = pool[0];
            const nudgeTitle = (picked.title ?? '') as string;
            const nudgeText = (picked.content ?? picked.nudgeText ?? picked.nudge_text ?? '') as string;
            const nudgeType = (picked.nudgeType ?? picked.nudge_type ?? picked.category ?? '') as string;
            const followupPrompts = (picked.followupPrompts ?? picked.followup_prompts ?? []) as string[];
            // Include second nudge for main chat display
            const second = pool.length > 1 ? pool[1] : null;
            const secondNudgeText = second ? (second.content ?? second.nudgeText ?? second.nudge_text ?? second.title ?? '') as string : '';
            const secondNudgeType = second ? (second.nudgeType ?? second.nudge_type ?? second.category ?? '') as string : '';
            setNudge({
              nudgeText: nudgeText || nudgeTitle, nudgeType,
              ...(nudgeTitle ? { title: nudgeTitle } : {}),
              ...(followupPrompts.length > 0 ? { followup_prompts: followupPrompts } : {}),
              ...(secondNudgeText ? { secondNudgeText, secondNudgeType } : {}),
            } as CoachNudgeResponse & { title?: string; followup_prompts?: string[]; secondNudgeText?: string; secondNudgeType?: string });
          } else {
            const agentNudge = await coachApi.getNudge(uid).catch(() => null);
            if (agentNudge) setNudge(agentNudge);
          }
        } catch { /* nudge load is non-critical */ }

        // Load goals: try bio.professionalGoals first (from onboarding)
        try {
          const loadedProfile = profileRes as UserProfile | null;
          const bioAny = loadedProfile?.bio as Record<string, unknown> | undefined;
          const bioGoals = (bioAny?.professionalGoals ?? bioAny?.professional_goals ?? []) as string[];
          if (bioGoals.length > 0) {
            // Parse type prefix: "[SHORT_TERM] title" or "[LONG_TERM] title"
            const shortTermGoals: { title: string; description: string }[] = [];
            const longTermGoals: { title: string; description: string }[] = [];
            for (const raw of bioGoals) {
              const match = raw.match(/^\[(SHORT_TERM|LONG_TERM)]\s*(.+)$/);
              if (match) {
                const [, type, title] = match;
                (type === 'LONG_TERM' ? longTermGoals : shortTermGoals).push({ title, description: '' });
              } else {
                // No prefix — default to short-term
                shortTermGoals.push({ title: raw, description: '' });
              }
            }
            setGoals({ shortTermGoals, longTermGoals } as CoachGoalsResponse);
          } else {
            const agentGoals = await coachApi.getGoals(uid).catch(() => null);
            if (agentGoals) setGoals(agentGoals);
          }
        } catch { /* goals load is non-critical */ }

        const sid = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `session-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        const stored = loadSessionFromStorage();

        // Determine conversation to resume: always land on latest if not expired (within 7 days)
        let convToResume: ConversationListItem | null = null;
        let convIdToTry: string | null = null;

        // 1. If stored conv not in API list (e.g. just finished streaming), prefer it
        if (stored?.userId === uid && stored.conversationId) {
          const found = convListArr.find((c) => getConvId(c as ConversationListItem & { _id?: string; conversation_id?: string }) === stored.conversationId);
          if (!found) {
            convIdToTry = stored.conversationId;
          }
        }

        // 2. Otherwise use most recent from API within 7 days (always land on latest if not expired)
        if (!convIdToTry) {
          convToResume = getMostRecentConversationWithinDays(convListArr, AUTO_RESUME_DAYS);
          if (!convToResume && convListArr.length > 0 && isResumableStatus(convListArr[0].status)) {
            convToResume = convListArr[0];
          }
        }

        // 3. Resume existing (prefer stored; never create new on refresh when we have one to resume)
        const idToResume = convIdToTry ?? (convToResume ? getConvId(convToResume as ConversationListItem & { _id?: string; conversation_id?: string }) || convToResume.id : null);
        if (idToResume) {
          setMessagesLoadAttemptedForCurrentConv(true);
          try {
            const session = await coachApi.startSession({
              userId: uid,
              sessionId: sid,
              conversationId: idToResume,
            });
            if (cancelled) return;
            setSessionId(session.sessionId);
            setConversationId(session.conversationId ?? idToResume);
            saveSessionToStorage({
              sessionId: session.sessionId,
              conversationId: session.conversationId ?? idToResume,
              userId: uid,
            });
            const convIdForMsgs = session.conversationId ?? idToResume;
            const msgRes = await coachApi.getConversationMessages(convIdForMsgs);
            if (!cancelled) {
              const res = msgRes as unknown as Record<string, unknown> | null;
              const initArr = (res != null && typeof res === 'object'
                ? res.messages ?? res.content ?? res.data ?? res.items ?? res.results
                : undefined) as unknown[] | undefined;
              const rawArr = Array.isArray(initArr) ? initArr : [];
              const mapped: CoachMessageDisplay[] = rawArr.map((m) => {
                const msg = m as Record<string, unknown>;
                const meta = (msg.metadata ?? {}) as Record<string, unknown>;
                const src = meta.sources as { type?: string; id?: string; title?: string }[] | undefined;
                const fu = (meta.suggestedFollowUps ?? meta.suggested_follow_ups) as string[] | undefined;
                const sources: { type: string; id: string; title: string }[] | undefined = Array.isArray(src)
                  ? src
                      .filter((s): s is { type?: string; id: string; title?: string } => !!s?.id)
                      .map((s) => ({ type: s.type ?? '', id: s.id, title: s.title ?? '' }))
                  : undefined;
                return {
                  role: (msg.role === 'USER' ? 'user' : 'assistant') as 'user' | 'assistant',
                  content: String(msg.content ?? msg.text ?? ''),
                  messageId: (msg.id ?? msg.messageId ?? msg._id) as string | undefined,
                  sources,
                  followUps: Array.isArray(fu) ? fu : undefined,
                };
              });
              setMessages(mapped);
              // Retry if empty — backend persists messages async; longer delays help Docker/slow backends
              if (mapped.length === 0) {
                const retryDelays = [2000, 4000, 6000, 10000, 20000];
                let retryIdx = 0;
                const scheduleRetry = () => {
                  if (retryIdx >= retryDelays.length) return;
                  const delay = retryDelays[retryIdx++];
                  retryTimer = setTimeout(async () => {
                    if (cancelled) return;
                    try {
                      const retryRes = await coachApi.getConversationMessages(convIdForMsgs);
                      const retRes = retryRes as unknown as Record<string, unknown> | null;
                      const retryRaw = (retRes != null && typeof retRes === 'object'
                        ? retRes.messages ?? retRes.content ?? retRes.data ?? retRes.items ?? retRes.results
                        : undefined) as unknown[] | undefined;
                      const retryArr = Array.isArray(retryRaw) ? retryRaw : [];
                      const retryMapped: CoachMessageDisplay[] = retryArr.map((m) => {
                        const msg = m as Record<string, unknown>;
                        const meta = (msg.metadata ?? {}) as Record<string, unknown>;
                        const src = meta.sources as { type?: string; id?: string; title?: string }[] | undefined;
                        const fu = (meta.suggestedFollowUps ?? meta.suggested_follow_ups) as string[] | undefined;
                        const sources: { type: string; id: string; title: string }[] | undefined = Array.isArray(src)
                          ? src
                              .filter((s): s is { type?: string; id: string; title?: string } => !!s?.id)
                              .map((s) => ({ type: s.type ?? '', id: s.id, title: s.title ?? '' }))
                          : undefined;
                        return {
                          role: (msg.role === 'USER' ? 'user' : 'assistant') as 'user' | 'assistant',
                          content: String(msg.content ?? msg.text ?? ''),
                          messageId: (msg.id ?? msg.messageId ?? msg._id) as string | undefined,
                          sources,
                          followUps: Array.isArray(fu) ? fu : undefined,
                        };
                      });
                      if (!cancelled && retryMapped.length > 0) {
                        setMessages(retryMapped);
                      } else if (!cancelled) {
                        scheduleRetry();
                      }
                    } catch {
                      if (!cancelled) scheduleRetry();
                    }
                  }, delay);
                };
                scheduleRetry();
              }
            }
            if (!cancelled && convIdToTry && !convListArr.some((c) => c.id === idToResume)) {
              coachApi.getConversations(uid, { limit: 50 })
                .then((r) => {
                  if (!cancelled) {
                    const list = r.conversations ?? (r as { content?: unknown[] }).content ?? [];
                    setAllConversations(sortConversationsByRecency(Array.isArray(list) ? list : []));
                  }
                })
                .catch(() => {});
            }
          } catch {
            // Do not clear or overwrite storage on resume failure — allow retry on next refresh.
            // Fallback: start new session for this tab only (do NOT persist — keeps old conv for retry)
            setMessagesLoadAttemptedForCurrentConv(false);
            try {
              const session = await coachApi.startSession({ userId: uid, sessionId: sid });
              if (cancelled) return;
              setSessionId(session.sessionId);
              setConversationId(session.conversationId ?? null);
              // Intentionally do NOT saveSessionToStorage — keep stored conversationId for retry on next refresh
            } catch (fallbackErr) {
              if (!cancelled) setError(fallbackErr instanceof Error ? fallbackErr.message : 'Failed to start session');
            }
          }
        } else {
          // No recent conversation: start brand new session (creates new empty conversation)
          setMessagesLoadAttemptedForCurrentConv(false);
          const session = await coachApi.startSession({ userId: uid, sessionId: sid });
          if (cancelled) return;
          setSessionId(session.sessionId);
          setConversationId(session.conversationId ?? null);
          if (session.conversationId) {
            saveSessionToStorage({
              sessionId: session.sessionId,
              conversationId: session.conversationId,
              userId: uid,
            });
          }
          const refetch = await coachApi.getConversations(uid, { limit: 50 });
          if (!cancelled) {
            const list = refetch.conversations ?? (refetch as { content?: unknown[] }).content ?? [];
            setAllConversations(sortConversationsByRecency(Array.isArray(list) ? list : []));
          }
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to start coach');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [canonicalUserId, sortConversationsByRecency]);

  const sendUserMessage = useCallback((text: string) => {
    sendUserMessageRef.current?.(text);
  }, []);

  const registerSendUserMessage = useCallback((handler: ((text: string) => void) | null) => {
    sendUserMessageRef.current = handler;
  }, []);

  // Refetch conversations when userId is available. Folder filtering is done client-side.
  useEffect(() => {
    if (!userId) return;
    refetchConversations();
  }, [userId, refetchConversations]);

  const planId = dashboard?.activePlan?.id ?? (learningPlan as { id?: string } | null)?.id ?? '';
  const totalDaysForPlan = dashboard?.totalDays ?? ((learningPlan?.learningModules?.[0]?.dailyContents?.length ?? 0) || 7);

  useEffect(() => {
    if (!userId || !planId || totalDaysForPlan < 1) return;
    let cancelled = false;
    const load = async () => {
      const next: Record<number, string> = {};
      for (let d = 1; d <= totalDaysForPlan; d++) {
        if (cancelled) return;
        try {
          const list = await userLessonApi.getByPlanAndDay(userId, planId, d);
          const title = list?.[0]?.title;
          if (title) next[d] = title;
        } catch {
          // ignore per-day failure
        }
      }
      if (!cancelled) setLessonTitlesByDay((prev) => ({ ...prev, ...next }));
    };
    load();
    return () => { cancelled = true; };
  }, [userId, planId, totalDaysForPlan]);

  const value = useMemo<CoachContextValue>(
    () => ({
      userId,
      profile,
      learningPlan,
      dashboard,
      activeWins,
      primaryWin,
      sessionId,
      conversationId,
      folders,
      selectedFolderId,
      conversations,
      messages,
      nudge,
      goals,
      loading,
      error,
      suggestedQuestions,
      messagesLoadAttemptedForCurrentConv,
      messagesLoadError,
      lessonTitlesByDay,
      setMessages,
      refetchNudge,
      refetchGoals,
      refetchFolders,
      refetchDashboard,
      refetchConversations,
      setSelectedFolderId,
      searchConversations,
      loadMessagesForConversation,
      startNewChat,
      selectConversation,
      persistSessionFromDone,
      updateConversationTitle,
      deleteConversation,
      sendUserMessage,
      registerSendUserMessage,
    }),
    [userId, profile, learningPlan, dashboard, activeWins, primaryWin, sessionId, conversationId, folders, selectedFolderId, conversations, messages, nudge, goals, loading, error, suggestedQuestions, messagesLoadAttemptedForCurrentConv, messagesLoadError, lessonTitlesByDay, refetchNudge, refetchGoals, refetchFolders, refetchDashboard, refetchConversations, setSelectedFolderId, searchConversations, loadMessagesForConversation, startNewChat, selectConversation, persistSessionFromDone, updateConversationTitle, deleteConversation, sendUserMessage, registerSendUserMessage]
  );

  return (
    <CoachContext.Provider value={value}>
      {children}
    </CoachContext.Provider>
  );
};
